// src/hooks/useScheduledReports.ts
// Scheduled report deliveries (via 3-dot menu > Schedule report).

import { useCallback, useState } from 'react';
import type { ScheduledReport } from '../pages/Accounting/Reports/AccountList/types';

interface UseScheduledReportsReturn {
  data: ScheduledReport[];
  loading: boolean;
  error: Error | null;
  addSchedule: (input: Omit<ScheduledReport, 'id' | 'active'>) => ScheduledReport;
  removeSchedule: (id: string) => void;
  toggleSchedule: (id: string) => void;
  refetch: () => void;
}

const STORAGE_KEY = 'novala:scheduled-reports:account-list';

function readFromStorage(): ScheduledReport[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ScheduledReport[];
  } catch (e) {
    // ignore
  }
  return [];
}

function writeToStorage(items: ScheduledReport[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore
  }
}

export function useScheduledReports(): UseScheduledReportsReturn {
  const [data, setData] = useState<ScheduledReport[]>(function () { return readFromStorage(); });
  const [loading] = useState(false);
  const [error] = useState<Error | null>(null);

  const addSchedule = useCallback(function (input: Omit<ScheduledReport, 'id' | 'active'>) {
    const next: ScheduledReport = {
      ...input,
      id: 'sch_' + Date.now().toString(36),
      active: true,
    };
    setData(function (prev) {
      const out = [next, ...prev];
      writeToStorage(out);
      return out;
    });
    return next;
  }, []);

  const removeSchedule = useCallback(function (id: string) {
    setData(function (prev) {
      const out = prev.filter(function (s) { return s.id !== id; });
      writeToStorage(out);
      return out;
    });
  }, []);

  const toggleSchedule = useCallback(function (id: string) {
    setData(function (prev) {
      const out = prev.map(function (s) { return s.id === id ? { ...s, active: !s.active } : s; });
      writeToStorage(out);
      return out;
    });
  }, []);

  const refetch = useCallback(function () {
    setData(readFromStorage());
  }, []);

  return { data, loading, error, addSchedule, removeSchedule, toggleSchedule, refetch };
}